package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.zq.astrock_ai.api.LicensePlan
import org.zq.astrock_ai.api.dto.LicenseGenerateResponseDto
import org.zq.astrock_ai.network.MarketApiClient
import org.zq.astrock_ai.platform.ClipboardHelper
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.adaptiveDialogWidth
import org.zq.astrock_ai.ui.research.gushi.data.AuthSessionStore

@Composable
fun LicenseGenerateDialog(
    onDismiss: () -> Unit,
) {
    val api = remember { MarketApiClient() }
    val scope = rememberCoroutineScope()
    var plan by remember { mutableStateOf(LicensePlan.MONTH) }
    var count by remember { mutableIntStateOf(1) }
    var loading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    var codes by remember { mutableStateOf<List<String>>(emptyList()) }
    var copiedHint by remember { mutableStateOf("") }
    val shape = RoundedCornerShape(14.dp)
    val codesText = remember(codes) { codes.joinToString("\n") }

    fun tryCopy(text: String, okHint: String) {
        val ok = ClipboardHelper.copyText(text)
        copiedHint = if (ok) okHint else "自动复制失败：请在下方框内长按/拖选后复制"
    }

    Dialog(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(420.dp)
                .background(Color.White, shape)
                .border(BorderStroke(1.dp, StockColors.Border), shape)
                .padding(20.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Text(
                "生成认证码",
                color = StockColors.TextPrimary,
                fontWeight = FontWeight.Bold,
                fontSize = 17.sp,
            )
            Spacer(Modifier.height(6.dp))
            Text(
                "写入数据库 license_code，一码一次。请把生成的码发给用户核销。",
                color = StockColors.TextSecondary,
                fontSize = 12.sp,
            )
            Spacer(Modifier.height(14.dp))
            Text("档位", color = StockColors.TextSecondary, fontSize = 12.sp)
            Spacer(Modifier.height(8.dp))
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    LicensePlan.entries.take(3).forEach { item ->
                        PlanChip(item = item, selected = item == plan, enabled = !loading) {
                            plan = item
                            error = ""
                            copiedHint = ""
                        }
                    }
                }
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    LicensePlan.entries.drop(3).forEach { item ->
                        PlanChip(item = item, selected = item == plan, enabled = !loading) {
                            plan = item
                            error = ""
                            copiedHint = ""
                        }
                    }
                }
            }
            Spacer(Modifier.height(12.dp))
            Row {
                Text(
                    "数量 $count",
                    color = StockColors.TextPrimary,
                    fontSize = 13.sp,
                    modifier = Modifier.weight(1f),
                )
                Text(
                    "−",
                    color = StockColors.PrimaryBlue,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier
                        .clip(RoundedCornerShape(8.dp))
                        .clickable(enabled = !loading && count > 1) { count -= 1 }
                        .padding(horizontal = 10.dp, vertical = 4.dp),
                )
                Text(
                    "+",
                    color = StockColors.PrimaryBlue,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier
                        .clip(RoundedCornerShape(8.dp))
                        .clickable(enabled = !loading && count < 20) { count += 1 }
                        .padding(horizontal = 10.dp, vertical = 4.dp),
                )
            }
            if (error.isNotBlank()) {
                Spacer(Modifier.height(8.dp))
                Text(error, color = StockColors.UpRed, fontSize = 12.sp)
            }
            if (codes.isNotEmpty()) {
                Spacer(Modifier.height(12.dp))
                Text(
                    "认证码（可点选/拖选后复制；也可点右侧「复制」）",
                    color = StockColors.TextSecondary,
                    fontSize = 11.sp,
                )
                Spacer(Modifier.height(6.dp))
                // 只读输入框：手机可长按选中，桌面可拖选；外层 SelectionContainer 双保险
                SelectionContainer {
                    BasicTextField(
                        value = codesText,
                        onValueChange = {},
                        readOnly = true,
                        textStyle = TextStyle(
                            color = StockColors.TextPrimary,
                            fontSize = 14.sp,
                            fontWeight = FontWeight.SemiBold,
                            fontFamily = FontFamily.Monospace,
                            lineHeight = 22.sp,
                        ),
                        cursorBrush = SolidColor(StockColors.PrimaryBlue),
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(min = 72.dp, max = 220.dp)
                            .background(Color(0xFFF8F9FB), RoundedCornerShape(8.dp))
                            .border(BorderStroke(1.dp, StockColors.Border), RoundedCornerShape(8.dp))
                            .padding(10.dp)
                            .verticalScroll(rememberScrollState()),
                    )
                }
                Spacer(Modifier.height(8.dp))
                codes.forEach { code ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(vertical = 2.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SelectionContainer(modifier = Modifier.weight(1f)) {
                            Text(
                                code,
                                color = StockColors.TextPrimary,
                                fontSize = 13.sp,
                                fontFamily = FontFamily.Monospace,
                                fontWeight = FontWeight.SemiBold,
                                maxLines = 1,
                            )
                        }
                        Text(
                            "复制",
                            color = StockColors.PrimaryBlue,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.SemiBold,
                            modifier = Modifier
                                .clip(RoundedCornerShape(8.dp))
                                .clickable { tryCopy(code, "已复制：$code") }
                                .padding(horizontal = 8.dp, vertical = 4.dp),
                        )
                    }
                }
                Spacer(Modifier.height(6.dp))
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Text(
                        "复制全部",
                        color = StockColors.PrimaryBlue,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier
                            .clip(RoundedCornerShape(8.dp))
                            .background(Color(0xFFE8F0FE))
                            .clickable { tryCopy(codesText, "已复制全部 ${codes.size} 条") }
                            .padding(horizontal = 10.dp, vertical = 6.dp),
                    )
                    if (copiedHint.isNotBlank()) {
                        Text(
                            copiedHint,
                            color = if (copiedHint.startsWith("已复制")) {
                                StockColors.DownGreen
                            } else {
                                StockColors.Orange
                            },
                            fontSize = 11.sp,
                            modifier = Modifier.weight(1f),
                        )
                    }
                }
            }
            Spacer(Modifier.height(16.dp))
            Row(modifier = Modifier.fillMaxWidth()) {
                OutlinedButton(
                    onClick = onDismiss,
                    enabled = !loading,
                    modifier = Modifier.weight(1f),
                ) {
                    Text("关闭")
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = {
                        val token = AuthSessionStore.load()?.token.orEmpty()
                        if (token.isBlank()) {
                            error = "请先登录"
                            return@Button
                        }
                        loading = true
                        error = ""
                        copiedHint = ""
                        scope.launch {
                            val result = runCatching {
                                api.generateLicense(plan.id, count, token)
                            }.getOrElse {
                                LicenseGenerateResponseDto(success = false, message = "网络异常或无发卡权限")
                            }
                            loading = false
                            if (result.success && result.codes.isNotEmpty()) {
                                codes = result.codes
                            } else {
                                error = result.message.ifBlank { "生成失败" }
                            }
                        }
                    },
                    enabled = !loading,
                    modifier = Modifier.weight(1f),
                    colors = ButtonDefaults.buttonColors(containerColor = StockColors.PrimaryBlue),
                ) {
                    Text(if (loading) "生成中…" else "生成")
                }
            }
        }
    }
}

@Composable
private fun PlanChip(
    item: LicensePlan,
    selected: Boolean,
    enabled: Boolean,
    onClick: () -> Unit,
) {
    Text(
        item.label,
        color = if (selected) Color.White else StockColors.PrimaryBlue,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        maxLines = 1,
        modifier = Modifier
            .clip(RoundedCornerShape(999.dp))
            .background(if (selected) StockColors.PrimaryBlue else Color(0xFFE8F0FE))
            .clickable(enabled = enabled, onClick = onClick)
            .padding(horizontal = 10.dp, vertical = 6.dp),
    )
}
